import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getOntologyChildren,
  getOntologyNode,
  getGenesForDisease,
  getDiseasesForGene,
  GENE_NODE_ID_PREFIX,
  searchOntologyGraph,
  isFlatOntologySearch,
} from '@/services/ontologyGraph.service';
import type { OntologyApiNode } from '@/types/ontologyGraph';
import type { Gene } from '@/types/ontologyGraph';
import type { OntologyNode } from '@/types/ontologyGraph';
import {
  isMoreNodeId,
  makeMoreNodeId,
  makeShowMoreGenesNodeId,
  isShowMoreGenesNodeId,
  makeShowRelatedDiseasesNodeId,
  makeOtherGenesNodeId,
  isOtherGenesNodeId,
} from '@/types/ontologyGraph';
import {
  INITIAL_GENE_LIMIT,
  GENE_BATCH_SIZE,
  OTHER_GENES_THRESHOLD,
} from '@/components/OntologyGraph/constants';

const MORE_CHILDREN_THRESHOLD = 10;

export type GraphEdge = { source: string; target: string };

export type UseOntologyGraphOptions = {
  rootNodeId?: string;
  rootNodeType?: 'id' | 'name';
  moreThreshold?: number;
  loadGenes?: boolean;
};

export type ShowMoreGenesChoice = 'next20' | 'manual' | 'all';

export type NodeLimitWarningChoice = 'continue' | 'cancel' | 'limit';

export type UseOntologyGraphReturn = {
  nodes: Map<string, OntologyNode>;
  edges: GraphEdge[];
  expandedNodes: Set<string>;
  moreListParentId: string | null;
  moreListChildren: { id: string; label: string }[];
  setMoreListParentId: (id: string | null) => void;
  openMoreList: (moreNodeId: string) => void;
  onExpandNode: (nodeId: string) => Promise<void>;
  collapseNode: (nodeId: string) => void;
  onSelectMoreChildren: (parentId: string, selectedIds: string[]) => Promise<void>;
  initialize: () => Promise<void>;
  initializeExplorer: (apiNodes: OntologyApiNode[]) => Promise<void>;
  resetToRoot: () => Promise<void>;
  loading: boolean;
  error: string | null;
  visibleGenesPerDisease: Map<string, number>;
  totalNodeCount: number;
  showMoreGenesDiseaseId: string | null;
  setShowMoreGenesDiseaseId: (id: string | null) => void;
  onShowMoreGenesChoice: (diseaseId: string, choice: ShowMoreGenesChoice) => Promise<void>;
  geneSelectionDiseaseId: string | null;
  setGeneSelectionDiseaseId: (id: string | null) => void;
  onAddSelectedGenes: (diseaseId: string, geneIds: string[]) => void;
  allGenesForDisease: (diseaseId: string) => Gene[] | undefined;
  nodeLimitWarning: boolean;
  setNodeLimitWarning: (v: boolean) => void;
  setPendingNodeLimitAction: (fn: (() => void) | null) => void;
  onNodeLimitChoice: (choice: NodeLimitWarningChoice) => void;
  onGeneClick: (geneNodeId: string) => void;
  onShowRelatedDiseasesClick: (geneNodeId: string) => Promise<void>;
  onOtherGenesExpand: (diseaseId: string) => Promise<void>;
  highlightedNodeId: string | null;
  setHighlightedNodeId: (id: string | null) => void;
};

function toInternalNode(
  id: string,
  label: string,
  parentId: string | undefined,
  hasChildren: boolean,
  numDescendants: number,
  expanded: boolean,
  cui?: string,
  geneCount?: number,
  type: 'disease' | 'gene' = 'disease',
): OntologyNode {
  const parents = parentId ? [parentId] : [];
  return {
    id,
    label,
    parents,
    children: [],
    hasChildren,
    hiddenChildrenCount: numDescendants > 0 ? numDescendants : undefined,
    ...(geneCount != null ? { geneCount } : {}),
    expanded,
    type,
    ...(cui ? { cui } : {}),
  };
}

function toGeneNodeId(geneId: string): string {
  return `${GENE_NODE_ID_PREFIX}${geneId}`;
}

export function useOntologyGraph(options: UseOntologyGraphOptions): UseOntologyGraphReturn {
  const { rootNodeId, rootNodeType = 'id', moreThreshold = MORE_CHILDREN_THRESHOLD } = options;
  const loadGenes = options.loadGenes !== false;
  const [nodes, setNodes] = useState<Map<string, OntologyNode>>(new Map());
  const nodesRef = useRef<Map<string, OntologyNode>>(new Map());
  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);
  const [edges, setEdges] = useState<GraphEdge[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [moreListParentId, setMoreListParentId] = useState<string | null>(null);
  const [moreListChildren, setMoreListChildren] = useState<{ id: string; label: string }[]>([]);
  const [moreChildrenByParent, setMoreChildrenByParent] = useState<
    Map<string, { id: string; label: string }[]>
  >(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [visibleGenesPerDisease, setVisibleGenesPerDisease] = useState<Map<string, number>>(new Map());
  const visibleGenesPerDiseaseRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    visibleGenesPerDiseaseRef.current = visibleGenesPerDisease;
  }, [visibleGenesPerDisease]);
  const allGenesByDiseaseRef = useRef<Map<string, Gene[]>>(new Map());
  const [showMoreGenesDiseaseId, setShowMoreGenesDiseaseId] = useState<string | null>(null);
  const [geneSelectionDiseaseId, setGeneSelectionDiseaseId] = useState<string | null>(null);
  const [nodeLimitWarning, setNodeLimitWarning] = useState(false);
  const pendingNodeLimitActionRef = useRef<(() => void) | null>(null);
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);

  const setPendingNodeLimitAction = useCallback((fn: (() => void) | null) => {
    pendingNodeLimitActionRef.current = fn;
  }, []);

  const totalNodeCount = nodes.size;

  const addNode = useCallback((node: OntologyNode, source?: string) => {
    setNodes(prev => {
      const next = new Map(prev);
      next.set(node.id, node);
      return next;
    });
    if (source !== undefined) {
      setEdges(prev =>
        prev.some(e => e.source === source && e.target === node.id)
          ? prev
          : [...prev, { source, target: node.id }],
      );
      setNodes(prev => {
        const parent = prev.get(source);
        if (!parent) return prev;
        const next = new Map(prev);
        const children = parent.children.includes(node.id) ? parent.children : [...parent.children, node.id];
        next.set(source, { ...parent, children });
        return next;
      });
    }
  }, []);

  const addMoreNode = useCallback(
    (parentId: string, count: number) => {
      const moreId = makeMoreNodeId(parentId);
      const moreNode: OntologyNode = {
        id: moreId,
        label: `${count} more`,
        parents: [parentId],
        children: [],
        hasChildren: false,
        expanded: false,
      };
      addNode(moreNode, parentId);
    },
    [addNode],
  );

  const removeMoreNode = useCallback((parentId: string) => {
    const moreId = makeMoreNodeId(parentId);
    setNodes(prev => {
      const next = new Map(prev);
      next.delete(moreId);
      return next;
    });
    setEdges(prev => prev.filter(e => e.target !== moreId));
  }, []);

  const removeNodeById = useCallback((nodeId: string) => {
    setNodes(prev => {
      const node = prev.get(nodeId);
      const parentId = node?.parents?.[0];
      const next = new Map(prev);
      next.delete(nodeId);
      if (parentId) {
        const parent = next.get(parentId);
        if (parent) next.set(parentId, { ...parent, children: parent.children.filter(c => c !== nodeId) });
      }
      return next;
    });
    setEdges(prev => prev.filter(e => e.source !== nodeId && e.target !== nodeId));
  }, []);

  const addGenesForDisease = useCallback(async (diseaseId: string) => {
    const genes = await getGenesForDisease(diseaseId);
    if (genes.length === 0) return;
    allGenesByDiseaseRef.current.set(diseaseId, genes);
    const visible = visibleGenesPerDiseaseRef.current.get(diseaseId) ?? INITIAL_GENE_LIMIT;
    const useOtherGenes = genes.length > OTHER_GENES_THRESHOLD;
    const directCount = useOtherGenes ? Math.min(20, genes.length) : Math.min(visible, genes.length);
    const directGenes = genes.slice(0, directCount);
    const showMoreNodeId = makeShowMoreGenesNodeId(diseaseId);
    const otherNodeId = makeOtherGenesNodeId(diseaseId);

    setNodes(prev => {
      const next = new Map(prev);
      const parent = next.get(diseaseId);
      const newGeneIds: string[] = [];
      for (const g of directGenes) {
        const geneNodeId = toGeneNodeId(g.id);
        if (next.has(geneNodeId)) {
          newGeneIds.push(geneNodeId);
          continue;
        }
        next.set(
          geneNodeId,
          toInternalNode(geneNodeId, g.symbol, diseaseId, false, 0, false, undefined, undefined, 'gene'),
        );
        newGeneIds.push(geneNodeId);
      }
      const childIds: string[] = [
        ...(parent?.children.filter(c => !isShowMoreGenesNodeId(c) && !isOtherGenesNodeId(c)) ?? []),
        ...newGeneIds,
      ];
      if (useOtherGenes) {
        const otherCount = genes.length - 20;
        if (!next.has(otherNodeId)) {
          next.set(otherNodeId, {
            id: otherNodeId,
            label: `Other genes (${otherCount})`,
            parents: [diseaseId],
            children: [],
            hasChildren: true,
            expanded: false,
            type: 'other_genes',
            meta: { diseaseId, count: otherCount },
          });
          childIds.push(otherNodeId);
        }
      } else if (genes.length > directCount) {
        if (!next.has(showMoreNodeId)) {
          next.set(showMoreNodeId, {
            id: showMoreNodeId,
            label: '+ Show more genes',
            parents: [diseaseId],
            children: [],
            hasChildren: false,
            expanded: false,
            type: 'show_more_genes',
            meta: { diseaseId },
          });
          childIds.push(showMoreNodeId);
        }
      }
      if (parent) next.set(diseaseId, { ...parent, children: [...new Set(childIds)] });
      return next;
    });
    setEdges(prev => {
      const existing = new Set(prev.map(e => `${e.source}\t${e.target}`));
      const added = directGenes
        .map(g => ({ source: diseaseId, target: toGeneNodeId(g.id) }))
        .filter(e => !existing.has(`${e.source}\t${e.target}`));
      if (useOtherGenes && !existing.has(`${diseaseId}\t${otherNodeId}`))
        added.push({ source: diseaseId, target: otherNodeId });
      else if (
        !useOtherGenes &&
        genes.length > directCount &&
        !existing.has(`${diseaseId}\t${showMoreNodeId}`)
      )
        added.push({ source: diseaseId, target: showMoreNodeId });
      return added.length ? [...prev, ...added] : prev;
    });
    setVisibleGenesPerDisease(prev => {
      const next = new Map(prev);
      if (!next.has(diseaseId)) next.set(diseaseId, directCount);
      return next;
    });
  }, []);

  const initialize = useCallback(async () => {
    if (!rootNodeId) return;
    setLoading(true);
    setError(null);
    try {
      const queriedNodes = await searchOntologyGraph({ disease: rootNodeId, ontoID: rootNodeType });

      if (isFlatOntologySearch(queriedNodes)) {
        const matched =
          rootNodeType === 'id'
            ? queriedNodes.filter(node => node.id === rootNodeId || node.cui === rootNodeId)
            : queriedNodes;
        const nodesToShow = matched.length > 0 ? matched : queriedNodes;
        const newNodes = new Map<string, OntologyNode>();
        for (const node of nodesToShow) {
          newNodes.set(
            node.id,
            toInternalNode(node.id, node.label, undefined, false, 0, false, node.cui, node.geneCount),
          );
        }
        setNodes(newNodes);
        setEdges([]);
        setExpandedNodes(new Set());
        setMoreListParentId(null);
        setMoreListChildren([]);
        setMoreChildrenByParent(new Map());
        return;
      }

      const root =
        queriedNodes.find(node => node.id === rootNodeId || node.cui === rootNodeId) ??
        (await getOntologyNode(rootNodeId)) ??
        queriedNodes.find(node => !node.hierarchicalParent) ??
        queriedNodes[0] ??
        null;
      if (!root) {
        setError('Root node not found');
        return;
      }
      const rootInternal = toInternalNode(
        root.id,
        root.label,
        root.hierarchicalParent,
        root.hasHierarchicalChildren,
        root.numHierarchicalDescendants,
        false,
        root.cui,
        root.geneCount,
      );
      setNodes(new Map([[root.id, rootInternal]]));
      setEdges([]);
      setExpandedNodes(new Set());
      if (loadGenes) await addGenesForDisease(root.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load graph');
    } finally {
      setLoading(false);
    }
  }, [rootNodeId, rootNodeType, loadGenes, addGenesForDisease]);

  const initializeExplorer = useCallback(
    async (apiNodes: OntologyApiNode[]) => {
      setLoading(true);
      setError(null);
      try {
        const newNodes = new Map<string, OntologyNode>();
        for (const node of apiNodes) {
          newNodes.set(
            node.id,
            toInternalNode(
              node.id,
              node.label,
              undefined,
              true,
              node.numHierarchicalDescendants,
              false,
              node.cui,
              node.geneCount,
            ),
          );
        }
        setNodes(newNodes);
        setEdges([]);
        setExpandedNodes(new Set());
        setMoreListParentId(null);
        setMoreListChildren([]);
        setMoreChildrenByParent(new Map());
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load explorer');
      } finally {
        setLoading(false);
      }
    },
    [addGenesForDisease],
  );

  const resetToRoot = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const root = await getOntologyNode(rootNodeId);
      if (!root) return;
      const rootInternal = toInternalNode(
        root.id,
        root.label,
        root.hierarchicalParent,
        root.hasHierarchicalChildren,
        root.numHierarchicalDescendants,
        false,
        root.cui,
        root.geneCount,
      );
      setNodes(new Map([[root.id, rootInternal]]));
      setEdges([]);
      setExpandedNodes(new Set());
      setMoreListParentId(null);
      setMoreListChildren([]);
      setMoreChildrenByParent(new Map());
      if (loadGenes) await addGenesForDisease(root.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reset');
    } finally {
      setLoading(false);
    }
  }, [rootNodeId, loadGenes, addGenesForDisease]);

  const onOtherGenesExpand = useCallback(
    async (diseaseId: string) => {
      const genes = allGenesByDiseaseRef.current.get(diseaseId) ?? [];
      const otherGenes = genes.slice(20);
      if (otherGenes.length === 0) return;
      const otherNodeId = makeOtherGenesNodeId(diseaseId);
      const visibleOther = visibleGenesPerDisease.get(`other_${diseaseId}`) ?? INITIAL_GENE_LIMIT;
      const toShow = otherGenes.slice(0, visibleOther);
      toShow.forEach(g => {
        const geneNodeId = toGeneNodeId(g.id);
        if (nodes.has(geneNodeId)) return;
        addNode(
          toInternalNode(geneNodeId, g.symbol, otherNodeId, false, 0, false, undefined, undefined, 'gene'),
          otherNodeId,
        );
      });
      setNodes(prev => {
        const next = new Map(prev);
        const other = next.get(otherNodeId);
        if (!other) return next;
        const childIds = [...other.children, ...toShow.map(g => toGeneNodeId(g.id))];
        next.set(otherNodeId, { ...other, children: [...new Set(childIds)], expanded: true });
        return next;
      });
      setEdges(prev => {
        const existing = new Set(prev.map(e => `${e.source}\t${e.target}`));
        const added = toShow
          .map(g => ({ source: otherNodeId, target: toGeneNodeId(g.id) }))
          .filter(e => !existing.has(`${e.source}\t${e.target}`));
        return added.length ? [...prev, ...added] : prev;
      });
      if (visibleOther < otherGenes.length) {
        const showMoreId = makeShowMoreGenesNodeId(`other_${diseaseId}`);
        if (!nodes.has(showMoreId)) {
          const synthetic: OntologyNode = {
            id: showMoreId,
            label: '+ Show more genes',
            parents: [otherNodeId],
            children: [],
            hasChildren: false,
            expanded: false,
            type: 'show_more_genes',
            meta: { diseaseId: `other_${diseaseId}` },
          };
          addNode(synthetic, otherNodeId);
        }
      }
      setVisibleGenesPerDisease(prev => new Map(prev).set(`other_${diseaseId}`, toShow.length));
      setExpandedNodes(prev => new Set(prev).add(otherNodeId));
    },
    [nodes, visibleGenesPerDisease, addNode],
  );

  const onExpandNode = useCallback(
    async (nodeId: string) => {
      if (isMoreNodeId(nodeId)) return;
      const node = nodes.get(nodeId);
      if (node?.type === 'other_genes' && node.meta?.diseaseId) {
        await onOtherGenesExpand(node.meta.diseaseId);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        setExpandedNodes(prev => new Set(prev).add(nodeId));
        setNodes(prev => {
          const next = new Map(prev);
          const n = next.get(nodeId);
          if (n) next.set(nodeId, { ...n, expanded: true });
          return next;
        });

        const children = await getOntologyChildren(nodeId);
        if (children.length === 0) {
          if (!loadGenes) {
            setNodes(prev => {
              const next = new Map(prev);
              const n = next.get(nodeId);
              if (n) next.set(nodeId, { ...n, expanded: false, hasChildren: false });
              return next;
            });
            return;
          }
          const genes = await getGenesForDisease(nodeId);
          if (genes.length === 0) {
            setNodes(prev => {
              const next = new Map(prev);
              const n = next.get(nodeId);
              if (n) next.set(nodeId, { ...n, expanded: false, hasChildren: false });
              return next;
            });
            return;
          }
          await addGenesForDisease(nodeId);
          return;
        }

        if (children.length > moreThreshold) {
          removeMoreNode(nodeId);
          addMoreNode(nodeId, children.length);
          setMoreChildrenByParent(prev => {
            const next = new Map(prev);
            next.set(
              nodeId,
              children.map(c => ({ id: c.id, label: c.label })),
            );
            return next;
          });
          if (loadGenes) await addGenesForDisease(nodeId);
        } else {
          setNodes(prev => {
            const next = new Map(prev);
            const parent = next.get(nodeId);
            if (!parent) return prev;
            const newChildIds: string[] = [];
            for (const c of children) {
              const id = String(c.id);
              if (next.has(id)) {
                newChildIds.push(id);
                continue;
              }
              next.set(
                id,
                toInternalNode(
                  c.id,
                  c.label,
                  nodeId,
                  c.hasHierarchicalChildren,
                  c.numHierarchicalDescendants,
                  false,
                  c.cui,
                  c.geneCount,
                ),
              );
              newChildIds.push(id);
            }
            next.set(nodeId, {
              ...parent,
              children: [...new Set([...parent.children, ...newChildIds])],
            });
            return next;
          });
          setEdges(prev => {
            const existing = new Set(prev.map(e => `${e.source}\t${e.target}`));
            const newEdges = children
              .map(c => ({ source: nodeId, target: String(c.id) }))
              .filter(e => !existing.has(`${e.source}\t${e.target}`));
            return newEdges.length ? [...prev, ...newEdges] : prev;
          });
          if (loadGenes) {
            await addGenesForDisease(nodeId);
            for (const c of children) {
              await addGenesForDisease(c.id);
            }
          }
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to expand node');
      } finally {
        setLoading(false);
      }
    },
    [nodes, addMoreNode, removeMoreNode, addGenesForDisease, onOtherGenesExpand, moreThreshold],
  );

  const openMoreList = useCallback(
    (moreNodeId: string) => {
      const parentId = moreNodeId.replace(/^__more_/, '');
      const children = moreChildrenByParent.get(parentId) ?? [];
      setMoreListChildren(children);
      setMoreListParentId(parentId);
    },
    [moreChildrenByParent],
  );

  const onSelectMoreChildren = useCallback(
    async (parentId: string, selectedIds: string[]) => {
      setMoreListParentId(null);
      removeMoreNode(parentId);
      setMoreChildrenByParent(prev => {
        const next = new Map(prev);
        next.delete(parentId);
        return next;
      });
      const parent = nodes.get(parentId);
      if (!parent) return;
      const toAdd = moreListChildren.filter(c => selectedIds.includes(c.id));

      await Promise.all(
        toAdd.map(async c => {
          const realNode = await getOntologyNode(c.id);
          const childInternal: OntologyNode = {
            id: c.id,
            label: c.label,
            parents: [parentId],
            children: [],
            hasChildren: realNode?.hasHierarchicalChildren ?? false,
            hiddenChildrenCount: realNode?.numHierarchicalDescendants ?? undefined,
            expanded: false,
            ...(realNode?.cui ? { cui: realNode.cui } : {}),
          };
          addNode(childInternal, parentId);
          if (loadGenes) await addGenesForDisease(c.id);
        }),
      );
    },
    [nodes, moreListChildren, loadGenes, removeMoreNode, addNode, addGenesForDisease],
  );

  const allGenesForDisease = useCallback((diseaseId: string): Gene[] | undefined => {
    return allGenesByDiseaseRef.current.get(diseaseId);
  }, []);

  const onNodeLimitChoice = useCallback((choice: NodeLimitWarningChoice) => {
    setNodeLimitWarning(false);
    const fn = pendingNodeLimitActionRef.current;
    pendingNodeLimitActionRef.current = null;
    if (choice === 'cancel') return;
    if (choice === 'limit') {
      if (fn) fn();
      return;
    }
    if (fn) fn();
  }, []);

  const onShowMoreGenesChoice = useCallback(
    async (diseaseId: string, choice: ShowMoreGenesChoice) => {
      setShowMoreGenesDiseaseId(null);
      const isOther = diseaseId.startsWith('other_');
      const realDiseaseId = isOther ? diseaseId.slice(6) : diseaseId;
      const allGenes = allGenesByDiseaseRef.current.get(realDiseaseId) ?? [];
      const genes = isOther ? allGenes.slice(20) : allGenes;
      const showMoreId = makeShowMoreGenesNodeId(diseaseId);
      const parentId = isOther ? makeOtherGenesNodeId(realDiseaseId) : diseaseId;
      const parent = nodes.get(parentId);
      if (!parent) return;

      const doAdd = (fromIndex: number, toIndex: number) => {
        const slice = genes.slice(fromIndex, toIndex);
        slice.forEach(g => {
          const geneNodeId = toGeneNodeId(g.id);
          if (nodes.has(geneNodeId)) return;
          addNode(
            toInternalNode(geneNodeId, g.symbol, parentId, false, 0, false, undefined, undefined, 'gene'),
            parentId,
          );
        });
        setNodes(prev => {
          const p = prev.get(parentId);
          if (!p) return prev;
          const next = new Map(prev);
          const newIds = slice.map(g => toGeneNodeId(g.id));
          next.set(parentId, {
            ...p,
            children: [...new Set([...p.children.filter(c => c !== showMoreId), ...newIds])],
          });
          return next;
        });
        setEdges(prev => {
          const existing = new Set(prev.map(e => `${e.source}\t${e.target}`));
          const added = slice
            .map(g => ({ source: parentId, target: toGeneNodeId(g.id) }))
            .filter(e => !existing.has(`${e.source}\t${e.target}`));
          return added.length ? [...prev, ...added] : prev;
        });
      };

      const visibleKey = isOther ? diseaseId : diseaseId;
      const visible = visibleGenesPerDisease.get(visibleKey) ?? (isOther ? 0 : INITIAL_GENE_LIMIT);
      if (choice === 'next20') {
        const newVisible = Math.min(visible + GENE_BATCH_SIZE, genes.length);
        setVisibleGenesPerDisease(prev => new Map(prev).set(diseaseId, newVisible));
        doAdd(visible, newVisible);
        if (newVisible >= genes.length) removeNodeById(showMoreId);
      } else if (choice === 'all') {
        setVisibleGenesPerDisease(prev => new Map(prev).set(diseaseId, genes.length));
        doAdd(visible, genes.length);
        removeNodeById(showMoreId);
      } else {
        setGeneSelectionDiseaseId(diseaseId);
      }
    },
    [nodes, visibleGenesPerDisease, addNode, removeNodeById],
  );

  const onAddSelectedGenes = useCallback(
    (diseaseId: string, geneIds: string[]) => {
      setGeneSelectionDiseaseId(null);
      const genes = allGenesByDiseaseRef.current.get(diseaseId) ?? [];
      const toAdd = genes.filter(g => geneIds.includes(g.id));
      const showMoreId = makeShowMoreGenesNodeId(diseaseId);
      toAdd.forEach(g => {
        const geneNodeId = toGeneNodeId(g.id);
        if (nodes.has(geneNodeId)) return;
        addNode(
          toInternalNode(geneNodeId, g.symbol, diseaseId, false, 0, false, undefined, undefined, 'gene'),
          diseaseId,
        );
      });
      setNodes(prev => {
        const p = prev.get(diseaseId);
        if (!p) return prev;
        const next = new Map(prev);
        const newIds = toAdd.map(g => toGeneNodeId(g.id));
        next.set(diseaseId, {
          ...p,
          children: [...new Set([...p.children.filter(c => c !== showMoreId), ...newIds])],
        });
        return next;
      });
      setEdges(prev => {
        const existing = new Set(prev.map(e => `${e.source}\t${e.target}`));
        const added = toAdd
          .map(g => ({ source: diseaseId, target: toGeneNodeId(g.id) }))
          .filter(e => !existing.has(`${e.source}\t${e.target}`));
        return added.length ? [...prev, ...added] : prev;
      });
      const newVisible = (visibleGenesPerDisease.get(diseaseId) ?? INITIAL_GENE_LIMIT) + toAdd.length;
      setVisibleGenesPerDisease(prev => new Map(prev).set(diseaseId, newVisible));
      const total = allGenesByDiseaseRef.current.get(diseaseId)?.length ?? 0;
      if (newVisible >= total) removeNodeById(showMoreId);
    },
    [nodes, visibleGenesPerDisease, addNode, removeNodeById],
  );

  const collapseNode = useCallback((nodeId: string) => {
    const currentNodes = nodesRef.current;
    const node = currentNodes.get(nodeId);
    if (!node) return;

    const toRemove = new Set<string>();
    const queue: string[] = [...node.children];
    while (queue.length > 0) {
      const id = queue.shift()!;
      if (toRemove.has(id)) continue;
      toRemove.add(id);
      const child = currentNodes.get(id);
      if (child?.children) queue.push(...child.children);
    }
    if (toRemove.size === 0) return;

    setNodes(prev => {
      const next = new Map(prev);
      for (const id of toRemove) next.delete(id);
      const parent = next.get(nodeId);
      if (parent) next.set(nodeId, { ...parent, children: [], expanded: false });
      return next;
    });
    setEdges(prev => prev.filter(e => !toRemove.has(e.target) && !toRemove.has(e.source)));
    setExpandedNodes(prev => {
      const next = new Set(prev);
      next.delete(nodeId);
      toRemove.forEach(id => next.delete(id));
      return next;
    });
  }, []);

  const onGeneClick = useCallback(
    (geneNodeId: string) => {
      const geneId = geneNodeId.startsWith(GENE_NODE_ID_PREFIX)
        ? geneNodeId.slice(GENE_NODE_ID_PREFIX.length)
        : geneNodeId;
      const relatedId = makeShowRelatedDiseasesNodeId(geneId);
      if (nodes.has(relatedId)) return;

      const geneNode = nodes.get(geneNodeId);
      if (!geneNode) return;
      const alreadyHasRelatedDiseases = geneNode.children.some(childId => {
        const child = nodes.get(childId);
        return child?.type === 'disease';
      });
      if (alreadyHasRelatedDiseases) return;

      const synthetic: OntologyNode = {
        id: relatedId,
        label: '+ Show related diseases',
        parents: [geneNodeId],
        children: [],
        hasChildren: false,
        expanded: false,
        type: 'show_related_diseases',
        meta: { geneId },
      };
      addNode(synthetic, geneNodeId);
    },
    [nodes, addNode],
  );

  const onShowRelatedDiseasesClick = useCallback(
    async (geneNodeIdOrRawId: string) => {
      const geneId = geneNodeIdOrRawId.startsWith(GENE_NODE_ID_PREFIX)
        ? geneNodeIdOrRawId.slice(GENE_NODE_ID_PREFIX.length)
        : geneNodeIdOrRawId;
      const graphGeneNodeId = geneNodeIdOrRawId.startsWith(GENE_NODE_ID_PREFIX)
        ? geneNodeIdOrRawId
        : toGeneNodeId(geneId);
      const relatedId = makeShowRelatedDiseasesNodeId(geneId);
      setLoading(true);
      setHighlightedNodeId(null);
      try {
        const diseases = await getDiseasesForGene(geneId);
        removeNodeById(relatedId);
        const alreadyExistingId = diseases.find(d => nodes.has(d.id))?.id ?? null;
        if (alreadyExistingId) setHighlightedNodeId(alreadyExistingId);
        diseases.forEach(d => {
          if (nodes.has(d.id)) return;
          const childNode: OntologyNode = {
            id: d.id,
            label: d.label,
            parents: [graphGeneNodeId],
            children: [],
            hasChildren: false,
            expanded: false,
            type: 'disease',
          };
          addNode(childNode, graphGeneNodeId);
        });
      } finally {
        setLoading(false);
      }
    },
    [nodes, addNode, removeNodeById],
  );

  return useMemo(
    () => ({
      nodes,
      edges,
      expandedNodes,
      moreListParentId,
      moreListChildren,
      setMoreListParentId,
      openMoreList,
      onExpandNode,
      collapseNode,
      onSelectMoreChildren,
      initialize,
      initializeExplorer,
      resetToRoot,
      loading,
      error,
      visibleGenesPerDisease,
      totalNodeCount,
      showMoreGenesDiseaseId,
      setShowMoreGenesDiseaseId,
      onShowMoreGenesChoice,
      geneSelectionDiseaseId,
      setGeneSelectionDiseaseId,
      onAddSelectedGenes,
      allGenesForDisease,
      nodeLimitWarning,
      setNodeLimitWarning,
      setPendingNodeLimitAction,
      onNodeLimitChoice,
      onGeneClick,
      onShowRelatedDiseasesClick,
      onOtherGenesExpand,
      highlightedNodeId,
      setHighlightedNodeId,
    }),
    [
      nodes,
      edges,
      expandedNodes,
      moreListParentId,
      moreListChildren,
      openMoreList,
      onExpandNode,
      collapseNode,
      onSelectMoreChildren,
      initialize,
      initializeExplorer,
      resetToRoot,
      loading,
      error,
      visibleGenesPerDisease,
      showMoreGenesDiseaseId,
      onShowMoreGenesChoice,
      geneSelectionDiseaseId,
      onAddSelectedGenes,
      allGenesForDisease,
      nodeLimitWarning,
      setPendingNodeLimitAction,
      onNodeLimitChoice,
      onGeneClick,
      onShowRelatedDiseasesClick,
      onOtherGenesExpand,
      highlightedNodeId,
    ],
  );
}
